import { PaymentProvider } from '@bumble/app/__generated__/data-contracts';
import AwsIcon from '@bumble/app/assets/aws-icon.svg';
import BumbleIcon from '@bumble/app/assets/bumble-icon.svg';
import { useState } from 'react';
// import StripeIcon from '@bumble/app/assets/stripe-icon.svg';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import Stripe from '@bumble/app/components/billing/Stripe';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  lighten,
  styled,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ReactSVG } from 'react-svg';
import { toast } from 'react-toastify';

const providerInfo = {
  [PaymentProvider.Stripe]: {
    icon: BumbleIcon,
    title: 'Stripe Payment',
  },
  [PaymentProvider.Aws]: {
    icon: AwsIcon,
    title: 'AWS Marketplace',
  },
};

interface BillingTypeProps {
  type: PaymentProvider;
}

export const BillingType = ({ type = PaymentProvider.Stripe }: BillingTypeProps) => {
  const { icon, title } = providerInfo[type];
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <ReactSVG
        src={icon}
        title={title}
        beforeInjection={(svg) => {
          svg.setAttribute('style', 'height: 32px');
        }}
      />
      <Typography variant="body2">{title}</Typography>
    </Box>
  );
};

interface PreferredMethodProps {
  type: PaymentProvider;
  refetch: () => void;
  preferred?: boolean;
}

export const PreferredMethod = ({ type, refetch, preferred = false }: PreferredMethodProps) => {
  const api = useApi();
  const setPreferred = useMutation(api.setPreferredBillingApiV1SetPreferredBillingPost, {
    onSuccess() {
      toast.success('Preferred billing method updated.');
      refetch();
    },
    onError: apiError,
  });
  const handleSetPreferred = () => {
    setPreferred.mutate({
      provider: type,
    });
  };
  if (preferred) {
    return <CheckCircleOutlineIcon color="primary" />;
  }
  return (
    <Button
      variant="text"
      color="inherit"
      size="small"
      sx={{ fontSize: '12px' }}
      onClick={handleSetPreferred}
    >
      Select as Preferred
    </Button>
  );
};

interface RemoveBillingProps {
  type: PaymentProvider;
  refetch: () => void;
}
export const RemoveBilling = ({ type, refetch }: RemoveBillingProps) => {
  const api = useApi();
  const removeBilling = useMutation(api.removeBillingApiV1RemoveBillingPost, {
    onSuccess() {
      toast.success('Billing method removed.');
      refetch();
    },
    onError: apiError,
  });

  const handleRemoveBilling = () => {
    removeBilling.mutate({
      provider: type,
    });
  };
  return (
    <IconButton
      aria-label="delete"
      sx={(theme) => ({ '&:hover': { color: theme.palette.primary.main } })}
      onClick={handleRemoveBilling}
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: lighten(theme.palette.primary.main, 0.95),
  },
  '&:hover': {
    backgroundColor: lighten(theme.palette.primary.main, 0.9),
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export const Billing = () => {
  const api = useApi();

  const [showStripe, setShowStripe] = useState(false);
  const handleShowStripe = () => setShowStripe(true);
  const handleCloseStripe = () => setShowStripe(false);

  const { isLoading, data, refetch } = useQuery(
    ['billing'],
    api.getBillingsApiV1GetBillingsGet.bind(null, {}),
    {
      enabled: true,
    },
  );

  return (
    <PageContent>
      <PageHeader pageName="Billing" />
      <Grid container spacing={5}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={5} sx={{ mb: 5 }}>
            <Grid item xs={12} md={6} sx={{ flexGrow: 1 }}>
              <Card variant="outlined" elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexFlow: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex', gap: 5, mb: 'auto' }}>
                    <ReactSVG
                      src={BumbleIcon}
                      style={{
                        padding: '10px',
                        width: '100%',
                        height: '100%',
                        maxWidth: 80,
                        maxHeight: 80,
                      }}
                    />
                    <Box sx={{ mb: 5 }}>
                      <Typography variant="subtitle1">Bumblebee Networks Platform</Typography>
                      <Typography variant="body2">
                        Setup payment directly on the Bumblebee Networks platform.
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleShowStripe}
                    sx={{ mt: 'auto' }}
                  >
                    Setup Payment
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6} sx={{ flexGrow: 1 }}>
              <Card variant="outlined" elevation={0} sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexFlow: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex', gap: 5, mb: 'auto' }}>
                    <ReactSVG
                      src={AwsIcon}
                      style={{
                        padding: '10px',
                        width: '100%',
                        height: '100%',
                        maxWidth: 80,
                        maxHeight: 80,
                      }}
                    />
                    <Box sx={{ mb: 5 }}>
                      <Typography variant="subtitle1">AWS Marketplace</Typography>
                      <Typography variant="body2">
                        Click the button below to be redirected to AWS Marketplace, and
                        subscribe to Bumblebee Networks SaaS product.
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    href="https://aws.amazon.com/marketplace/pp/prodview-wnzxaqufmv67w"
                    rel="noopener"
                    target="_blank"
                    fullWidth
                  >
                    Go to AWS Marketplace
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Card variant="outlined" elevation={0}>
            <CardContent>
              {isLoading ? (
                <Loading height={73} />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Provider</TableCell>
                        <TableCell>Subscription Info</TableCell>
                        <TableCell align="center">Preferred</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data?.data && data?.data.length > 0 ? (
                        data?.data.map(({ preferred, provider, subscription_info }) => (
                          <StyledTableRow key={provider}>
                            <TableCell>
                              <BillingType type={provider} />
                            </TableCell>
                            <TableCell>{subscription_info.customer_identifier}</TableCell>
                            <TableCell align="center">
                              <PreferredMethod
                                type={provider}
                                refetch={refetch}
                                preferred={preferred}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <RemoveBilling type={provider} refetch={refetch} />
                            </TableCell>
                          </StyledTableRow>
                        ))
                      ) : (
                        <StyledTableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" sx={{ py: 5 }}>
                              No billing methods found.
                            </Typography>
                          </TableCell>
                        </StyledTableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" elevation={0}>
            <CardContent>
              <Typography variant="subtitle1">How do I setup Billing?</Typography>
              <Typography variant="body2">
                Setting up your billing is straightforward and flexible. You have two convenient
                options:{' '}
                <Box
                  component="span"
                  sx={(theme) => ({ fontWeight: 700, color: theme.palette.primary.main })}
                >
                  Bumblebee Networks Platform
                </Box>{' '}
                or{' '}
                <Box
                  component="span"
                  sx={(theme) => ({ fontWeight: 700, color: theme.palette.primary.main })}
                >
                  AWS Marketplace
                </Box>
                . For a streamlined and secure payment process, Bumblebee Networks offers a
                user-friendly interface with payment functionalities. Alternatively, if you prefer
                the integration capabilities of AWS Marketplace, you can manage your billing through
                their platform. Choose the option that best suits your needs and once you decide,
                simply select your preferred billing method and follow the steps to get started.{' '}
                <Box
                  component="span"
                  sx={(theme) => ({ fontWeight: 700, color: theme.palette.primary.main })}
                >
                  For detailed step by step instruction,{' '}
                </Box>
                <Link
                  href="https://www.bumblebeenet.com/post/how-to-setup-billing"
                  target="_blank"
                  rel="noreferrer"
                >
                  click here
                </Link>
                .
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {showStripe && (
        <DialogStandard
          open={showStripe}
          handleClose={handleCloseStripe}
          title="Billing Information"
        >
          <Stripe onClose={handleCloseStripe} />
        </DialogStandard>
      )}
    </PageContent>
  );
};
